// 56-byte BITMAPV3INFOHEADER (BITMAPINFOHEADER + R/G/B/A bitfield masks).
//
// This is the most widely-compatible 32-bpp "BMP with alpha" header. The previous
// 108-byte BITMAPV4HEADER form renders correctly in Firefox but Chromium's BMP decoder
// ignores its alpha channel and treats the tile as fully opaque — so a mostly-transparent
// overlay tile (e.g. slope angle) came out as a solid BLACK tile in Chrome / Android WebView.
// The 56-byte V3 header (no colorspace block, explicit alpha mask) is honored by both
// engines. Pixel data is uncompressed BGRA, bottom-up — same cheap byte copy as before.
const BMP_FILE_HEADER_SIZE = 14
const DIB_HEADER_SIZE = 56
const HEADER_SIZE = BMP_FILE_HEADER_SIZE + DIB_HEADER_SIZE

export function encodeRGBA_BMP(
  pixels: Uint8ClampedArray,
  width: number,
  height: number
): ArrayBuffer {
  const rowBytes = width * 4
  const pixelDataSize = rowBytes * height
  const fileSize = HEADER_SIZE + pixelDataSize

  const buf = new ArrayBuffer(fileSize)
  const view = new DataView(buf)
  const u8 = new Uint8Array(buf)

  // BMP file header (14 bytes)
  view.setUint16(0, 0x424d, false) // 'BM'
  view.setUint32(2, fileSize, true)
  view.setUint32(6, 0, true) // reserved
  view.setUint32(10, HEADER_SIZE, true) // pixel data offset

  // BITMAPV3INFOHEADER (56 bytes = 40-byte BITMAPINFOHEADER + 4 DWORD masks)
  view.setUint32(14, DIB_HEADER_SIZE, true)
  view.setInt32(18, width, true)
  view.setInt32(22, height, true) // positive height => bottom-up rows
  view.setUint16(26, 1, true) // color planes
  view.setUint16(28, 32, true) // bits per pixel
  view.setUint32(30, 3, true) // compression = BI_BITFIELDS
  view.setUint32(34, pixelDataSize, true)
  view.setUint32(38, 2835, true) // X pixels per meter
  view.setUint32(42, 2835, true) // Y pixels per meter
  view.setUint32(46, 0, true) // colors in palette
  view.setUint32(50, 0, true) // important colors

  // Channel masks (V3: immediately after the 40-byte info header, at offset 54)
  view.setUint32(54, 0x00ff0000, true) // red mask
  view.setUint32(58, 0x0000ff00, true) // green mask
  view.setUint32(62, 0x000000ff, true) // blue mask
  view.setUint32(66, 0xff000000, true) // alpha mask

  // Pixel data: RGBA -> BGRA, written bottom-up (last source row first)
  let dst = HEADER_SIZE
  for (let row = height - 1; row >= 0; row--) {
    let src = row * rowBytes
    for (let col = 0; col < width; col++) {
      u8[dst] = pixels[src + 2] // B
      u8[dst + 1] = pixels[src + 1] // G
      u8[dst + 2] = pixels[src] // R
      u8[dst + 3] = pixels[src + 3] // A
      dst += 4
      src += 4
    }
  }

  return buf
}
